/* eslint-disable react/react-in-jsx-scope */
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { injectIntl, FormattedMessage } from 'react-intl';
import { createForm } from 'rc-form';
import { Grid, Typography } from '@material-ui/core';

import { GuideSubType } from 'yeself-common/typings/guide/Guide';
import { LayerType } from 'yeself-common/typings/guide/LayerCommon';

import { selectFeatureFlags } from 'yeself-client-common/featureFlags/FeatureFlagsSelector';

import { selectApplicationSettingsData } from 'Console/data/applicationDetail/settings/SettingsSelector';

import Breadcrumb from 'Console/components/common/Breadcrumb';
import GuideSearch from 'Console/components/applications/detail/guides/GuideSearch';
import DrawerWrapper from 'Console/components/common/drawer/DrawerWrapper';
import CreateGuideForm from 'Console/components/applications/detail/guides/CreateGuideForm';
import GuidesList from 'Console/components/applications/detail/guides/GuidesList';

import { selectContentLanguageCode, selectContentLanguage } from 'Console/data/app/selectors';
import { selectApplications } from 'Console/data/applications/selectors';
import {
    selectSortedApplicationGuidesList,
    selectApplicationGuidesAreLoading,
    selectGuidesSearchTerm,
} from 'Console/data/applicationDetail/guides/selectors';
import {
    fetchApplicationGuides,
    setGuidesSearchTerm,
    removeGuide,
    editGuideHost,
    duplicateGuide,
    updateGuide,
    createNewGuide,
} from 'Console/data/applicationDetail/guides/actions';
import { ROUTES } from 'Console/constants/routes';

import CreateGuideButton from './CreateGuideButton';

const styles = (theme) => ({
    root: {
        flexGrow: 1,
        alignItems: 'center',
        padding: '0 24px',
    },
    formWrapper: {
        width: '70%',
        margin: 'auto',
    },
    contentBox: {
        marginTop: theme.typography.pxToRem(40),
    },
    guideItemWidthFull: {
        width: '100%',
    },
    title: {
        paddingBottom: theme.typography.pxToRem(16),
    },
    btnBox: {
        paddingBottom: theme.typography.pxToRem(16),
        width: 'auto',
    },
    drawerWidth: {
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
});

export const initialSegmentsValues = {
    segmentCountry: {
        austria: false,
        malaysia: false,
        slovakia: false,
    },
    segmentReleaseDate: {
        timeRange: {
            gte: Date.now(),
            lte: Date.now(),
        },
        type: 'quick',
        label: { formatedLabel: '', label: '' },
    },
    segmentVersion: {
        one: false,
        two: false,
    },
    segmentTestGroup: {
        from: 1,
        to: 1,
    },
    guideType: {
        collector: false,
        tour: false,
    },
    guideCreateDate: {
        startDate: new Date(),
        endDate: new Date(),
    },
    guideIssues: {
        none: false,
        lowPerformers: false,
        healthProblems: false,
    },
};

const GuidesContainer = ({
    classes,
    intl,
    match,
    form,
    guide,
    form: { getFieldsValue, getFieldError, getFieldsError, setFieldsValue },
}) => {
    const [state, setState] = useState<{
        isDrawerWrapperShown: boolean;
        createGuideType: string | null;
        createGuideSubType: GuideSubType;
    }>({
        isDrawerWrapperShown: false,
        createGuideType: null,
        createGuideSubType: null,
    });

    const contentLanguageCode = useSelector(selectContentLanguageCode);
    const contentLanguage = useSelector(selectContentLanguage);
    const applicationsList = useSelector(selectApplications);
    const data = useSelector(selectApplicationSettingsData);
    const guidesAreLoading = useSelector(selectApplicationGuidesAreLoading);
    const guideList = useSelector(selectSortedApplicationGuidesList);
    const searchTerm = useSelector(selectGuidesSearchTerm);
    const featureFlags = useSelector(selectFeatureFlags);

    const dispatch = useDispatch();

    useEffect(() => {
        if (match.params && match.params.appId) {
            dispatch(fetchApplicationGuides(match.params.appId));
        }
    }, [match, dispatch]);

    const { isDrawerWrapperShown, createGuideType, createGuideSubType } = state;

    const handleGuideSearch = (event) => {
        dispatch(setGuidesSearchTerm(event.target.value));
    };

    const onClickMenuItem = (guideType: LayerType, guideSubType: GuideSubType) => {
        setState({
            isDrawerWrapperShown: true,
            createGuideType: guideType,
            createGuideSubType: guideSubType,
        });
    };

    const closeDrawerWrapper = () => {
        setState((values) => ({ ...values, isDrawerWrapperShown: false }));

        setFieldsValue({
            name: '',
            initialUrl: '',
            description: '',
        });
    };

    const { formatMessage } = intl;

    const createGuide = () => {
        const newGuide = getFieldsValue(['name', 'initialUrl', 'description']);
        newGuide.type = createGuideType;
        newGuide.subType = createGuideSubType;

        const errors: any[] = [];

        for (const error in getFieldsError()) {
            if (getFieldError(error)) {
                errors.push(getFieldError(error));
            }
        }

        // @TODO create errors
        // if (errors.length > 0) {
        //     return;
        // }

        dispatch(
            createNewGuide(newGuide, {
                success: `${formatMessage({ id: 'Notification.create.guide' })}`,
                fail: `${formatMessage({ id: 'Notification.fail' })}`,
            })
        );

        setFieldsValue({
            name: '',
            initialUrl: '',
            description: '',
        });
        closeDrawerWrapper();
    };

    const values = getFieldsValue(['name', 'initialUrl']);

    return (
        <Grid container className={classes.root}>
            <Grid className={classes.drawerWidth}>
                <DrawerWrapper
                    title={
                        <FormattedMessage id="Guides.create.new.guide" defaultMessage="Create guide" />
                    }
                    name={<FormattedMessage id="Buttons.cancel" defaultMessage="Cancel" />}
                    term={<FormattedMessage id="Buttons.create" defaultMessage="Create" />}
                    isShown={isDrawerWrapperShown}
                    onClose={closeDrawerWrapper}
                    confirmDisabled={!values.name}
                    onConfirm={createGuide}
                >
                    <Grid container className={classes.formWrapper}>
                        <CreateGuideForm
                            intl={intl}
                            guide={guide}
                            form={form}
                            contentLanguageCode={contentLanguageCode}
                        />
                    </Grid>
                </DrawerWrapper>
            </Grid>
            <Grid item xs={12}>
                <Typography variant="h6">
                    <Grid item xs={12}>
                        <Breadcrumb
                            paths={[
                                {
                                    to: ROUTES.APPLICATIONS,
                                    text: formatMessage({ id: 'Navigation.applications' }),
                                },
                                {
                                    text: data.name,
                                },
                            ]}
                        />
                    </Grid>
                </Typography>

                <Grid container direction="row" justify="space-between">
                    <Grid item className={classes.title}>
                        <Typography variant="h6">
                            <FormattedMessage
                                id="Applications.navigation.detail.guides"
                                defaultMessage="Guides"
                            />
                        </Typography>
                    </Grid>
                    <Grid item container direction="row" className={classes.btnBox}>
                        <Grid item>
                            <CreateGuideButton onClickMenuItem={onClickMenuItem} />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Grid container justify="flex-start">
                <GuideSearch searchTerm={searchTerm} intl={intl} handleGuideSearch={handleGuideSearch} />
            </Grid>

            <Grid container direction="row" justify="space-around" className={classes.contentBox}>
                <Grid item className={classes.guideItemWidthFull}>
                    <GuidesList
                        contentLanguage={contentLanguage}
                        list={guideList}
                        isLoading={guidesAreLoading}
                        editGuideHost={editGuideHost}
                        duplicateGuide={duplicateGuide}
                        removeGuide={removeGuide}
                        applicationsList={applicationsList}
                        appId={match.params.appId}
                        updateGuide={updateGuide}
                        searchTerm={searchTerm}
                        featureFlags={featureFlags}
                    />
                </Grid>
            </Grid>
        </Grid>
    );
};

export default withStyles(styles)(injectIntl(createForm()(GuidesContainer)));
